import type { Timestamp } from 'firebase/firestore';

export type Role = 'admin' | 'lessee';

export type UserStatus =
  | 'pending_approval'
  | 'profile_incomplete'
  | 'verification_pending'
  | 'active'
  | 'suspended'
  | 'terminated';

export interface EmergencyContact {
  name: string;
  relationship: string;
  phone: string;
}

export interface PersonalInfo {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  /**
   * Street line only ("1369 Adams Ave, Apt 3414"). City/state/zip are separate
   * fields below. Optional on the type because five of the first six lessees
   * onboarded through the single-box version and their docs have no city/state/zip
   * — read them through `formatAddress()` in lib/address.ts, never raw, or the
   * lease party recital comes out truncated.
   */
  address: string;
  city?: string;
  state?: string;
  zip?: string;
  phone: string;
  emergencyContact: EmergencyContact;
}

export interface PilotInfo {
  certificateType: string;
  certificateNumber: string;
  certificateIssueDate: string;
  ratings: string[];
  medicalClass: '1st' | '2nd' | '3rd' | 'basicmed' | '';
  medicalIssueDate: string;
  medicalExpiration: string;
  medicalLimitations: string;
  lastBFR: string;
  bfrExpiration: string;
  lastIPC: string;
  ipcExpiration: string;
  totalHours: number;
  hoursIn172: number;
  hoursLast90: number;
  hoursLast12Months: number;
  // How long the pilot intends to lease — asked at onboarding so the owner can
  // weigh the setup cost (insurance naming, checkout, paperwork) against a
  // short-term renter. Casey, 2026-09-08.
  intendedLeaseDuration: '' | '1-3mo' | '3-6mo' | '6-12mo' | '12mo+';
  flyingGoal: string; // free text: what they're working toward
}

export const LEASE_DURATION_LABEL: Record<PilotInfo['intendedLeaseDuration'], string> = {
  '': '—',
  '1-3mo': '1–3 months',
  '3-6mo': '3–6 months',
  '6-12mo': '6–12 months',
  '12mo+': 'A year or more / ongoing',
};

export interface DisclosureItem {
  answer: 'yes' | 'no' | '';
  explanation: string;
}

export interface Disclosures {
  priorAccident: DisclosureItem;
  priorEnforcement: DisclosureItem;
  priorInsuranceRefusal: DisclosureItem;
  dui: DisclosureItem;
  pendingActions: DisclosureItem;
}

// Lessee's own renter's / non-owned aircraft liability coverage — required by
// lease §11.2. Structured so the owner can track limits + expiration, not just
// hold a JPEG.
export interface InsuranceInfo {
  carrier: string;
  policyNumber: string;
  liabilityLimit: number; // combined single limit, US dollars
  expirationDate: string;
  /** Policy inception (YYYY-MM-DD). Optional — older records only carry the expiry. */
  effectiveDate?: string;
  /** Per-passenger sublimit, US dollars (the number that is usually the small one). */
  passengerLimit?: number;
  /** Non-owned aircraft physical-damage limit, US dollars — lease §11.2(b) "Aircraft Damage Coverage". */
  aircraftDamageLimit?: number;
  /**
   * Owner's note about the policy itself — e.g. "Personal non-owned, excludes
   * paid instruction" or "via Skywatch". Shown to the owner only.
   */
  notes?: string;
}

export interface UserDoc {
  uid: string;
  email: string;
  role: Role;
  status: UserStatus;
  createdAt: Timestamp;
  approvedAt?: Timestamp;
  terminatedAt?: Timestamp;
  personal?: Partial<PersonalInfo>;
  pilot?: Partial<PilotInfo>;
  disclosures?: Partial<Disclosures>;
  insurance?: Partial<InsuranceInfo>;
  // Named on the AIRCRAFT's own policy by endorsement (named pilot and/or
  // approved instructor). Separate from renter's insurance, which §11.2 still
  // requires — the card says both so nobody reads one as the other.
  policyNamed?: {
    as: 'pilot' | 'instructor' | 'both';
    since?: string; // YYYY-MM-DD the endorsement took effect
    policyNumber?: string;
    through?: string; // YYYY-MM-DD the policy term ends
    notes?: string;
  };
  /** Lease effective date (YYYY-MM-DD). Starts the §11.2 14-day certificate clock. */
  leaseEffectiveDate?: string;
  /**
   * When the pilot tapped "Got it" on the dual-instruction-only notice. Server-set.
   * This is the point of the notice: not to nag, but so "I don't remember seeing
   * that" has a dated answer. Cleared when a certificate is filed.
   */
  soloNoticeAckAt?: Timestamp;
  /**
   * When the owner, as Named Insured, approved this pilot as a renter pilot under the
   * Policy's open-pilot provisions — set by the admin "Approve as renter pilot" action
   * on a submitted CFI checkout. The policy's open-pilot clause covers "any Student or
   * Renter pilot approved by the Named Insured"; this is the dated record of that
   * approval and of what it was based on (the checkout doc + filed PDF).
   */
  renterApprovalAt?: Timestamp;
  /**
   * What the approval rested on, in the owner's words — "flew with him 8/27",
   * "CFI checkout, Adam Fenton 9/2", "reviewed logbook + 44 hrs/90 days".
   *
   * PB214 asks only that the pilot be "approved by the Named Insured" and says
   * nothing about HOW, so this is the record of the owner's judgment. It is not a
   * carrier requirement; it exists so the basis is written down at the time rather
   * than reconstructed after a loss.
   */
  renterApprovalBasis?: string;
  // Resume state for the onboarding wizard — saved as the pilot advances so a
  // half-finished onboarding picks up exactly where they left off.
  onboardingDraft?: {
    step?: string;
    licenseExp?: string;
    badgeExp?: string;
  };
  submittedForVerificationAt?: Timestamp;
  onboardingCompletedAt?: Timestamp;
  // Billing profile: how this person's flights are billed.
  //   'lessee' (default) — billed per the plan below (standard or promo)
  //   'owner'            — never billed (set on the owner/admin)
  //   'cfi'              — an instructor: their own flying bills at their plan (promo
  //                        $75/hr), no monthly minimum. Checkouts are NOT free since
  //                        10/10/26 — the renter logs and pays the checkout flight.
  billingProfile?: 'lessee' | 'owner' | 'cfi';
  // Pricing plan for a lessee. 'standard' = $100/Hobbs-hr, NO monthly minimum,
  // month-to-month. 'promo' = a discounted lease (via a promo code like
  // "rightrudder"): $75/Hobbs-hr, NO monthly minimum, month-to-month. Authorized
  // by lease §5.7 ("Plans and Pricing; Lessor Discretion") + a signed addendum.
  // 'comped' = flights are complimentary (owner's guest, e.g. Tyson): recorded
  // for airframe time but NEVER charged. Admin-set only (Lessees → Plan).
  plan?: 'standard' | 'promo' | 'comped';
  // A promo code the lessee entered during onboarding (validated server-side).
  // The owner confirms the resulting plan at activation.
  promoCode?: string;
  stripeCustomerId?: string;
  paymentMethodOnFile?: boolean;
  cardBrand?: string;
  cardLast4?: string;
  leaseStartDate?: string;
  /**
   * When the fully-executed lease landed — BOTH signatures, Casey's countersignature
   * included. In-app signing was retired 2026-07-13 in favour of DocuSign, so nothing
   * writes this automatically; the admin sets it from the lessee detail page once the
   * completed envelope is back.
   *
   * This is the BOOKING gate (Casey, 2026-08-28): documents can be approved and the
   * account made active while the envelope is still out, but no booking may be made
   * until the lease is executed. Distinct from the renter's-insurance certificate,
   * which runs on the separate 14-day §11.2 clock and gates SOLO flight, not booking.
   */
  leaseSignedAt?: Timestamp;
  // When the final "you're all set" email went out. Recorded so the owner can
  // see at a glance that onboarding was closed off, and so the button can't be
  // fired twice by accident. Sent by hand — never automatically — because only
  // the owner can confirm the fully-executed envelope is actually in hand.
  readyEmailSentAt?: Timestamp;
  // Set for pilots who were onboarded before the welcome email existed — Casey
  // walked them through the airplane in person. Distinct from readyEmailSentAt on
  // purpose: recording a send that never happened would put a lie in their file.
  welcomeEmailWaivedAt?: Timestamp;
  leaseSignedVersion?: string; // LEASE_VERSION of the executed document
  billingDayOfMonth?: number;
  adminNotes?: string;
  twoFAEnabled?: boolean;
  paymentFailedFlag?: boolean;
  paymentPastDue?: boolean;
  // Server-managed: last currency-reminder level emailed per cert (so the daily
  // reminder doesn't re-spam). 0 ok · 1 soon · 2 now · 3 expired.
  currencyAlertLevels?: { medical?: number; bfr?: number };
  welcomeEmailSentAt?: Timestamp;
}

export interface OpenPilotWarranty {
  totalHoursMin?: number;
  makeAndModelHoursMin?: number;
  recentHoursMin?: number;
  notes?: string;
}

export interface AircraftDoc {
  aircraftId: string;
  tailNumber: string;
  make: string;
  model: string;
  year: number;
  serialNumber: string;
  engineMake?: string; // e.g. "Continental"
  engineModel?: string; // e.g. "O-300-D"
  engineSerial?: string; // e.g. "3588-9-7-D"
  // SMOH — hours since major overhaul. Stored as the TACH READING AT OVERHAUL, not
  // as a running total, so SMOH is always currentTach minus this and can never go
  // stale. Left undefined until the owner records it; the UI says "not recorded"
  // rather than showing a number derived from a guess.
  engineOverhaulTach?: number;
  engineOverhaulDate?: string; // YYYY-MM-DD
  engineTboHours?: number; // manufacturer TBO, e.g. 1800 for the Continental O-300

  /**
   * Current empty weight and CG, from the last weighing. Every pilot needs these
   * to work a weight-and-balance, and they were previously only on a sheet in the
   * airplane. `asOf` is deliberately optional — the N8366X sheet carries no date,
   * and inventing one on a legal weighing record would be worse than leaving it
   * blank.
   */
  weightBalance?: {
    emptyWeight: number; // lb
    emptyCg: number; // in aft of datum
    emptyMoment: number; // in-lb
    usefulLoad: number; // lb
    maxGrossWeight: number; // lb
    weighedBy?: string;
    asOf?: string; // YYYY-MM-DD
  };

  /**
   * Certificated limits from the Type Certificate Data Sheet (172C, approved
   * 18 Jul 1961). The CG envelope is expressed as its corner points; the TCDS
   * says "straight line variation between points given", so anything between two
   * points interpolates linearly.
   */
  tcds?: {
    vSpeedsKcas?: { maneuvering: number; maxStructuralCruise: number; neverExceed: number; flapsExtended: number };
    minFuelGrade?: string;
    cgEnvelope?: {
      normal?: Array<{ weight: number; fwd: number; aft: number }>;
      utility?: Array<{ weight: number; fwd: number; aft: number }>;
    };
    maxWeight?: { normal: number; utility: number };
    /** Loading stations, in inches aft of datum, straight off the TCDS. */
    stations?: {
      frontSeats: number;
      rearSeats: number;
      baggage: number;
      fuel: number;
      oil: number;
    };
    capacities?: {
      seats: number;
      maxBaggageLb: number;
      fuelGalTotal: number;
      fuelGalUsable: number;
      oilGal: number;
    };
    serialRange?: string;
  };

  /**
   * The owner's ForeFlight weight-and-balance profile, hosted for renters to import
   * rather than each building their own and getting a station arm wrong. ForeFlight
   * shares these from More > Aircraft > Send To; whatever that produces is stored
   * as-is and handed to iOS, which opens it in ForeFlight.
   */
  foreflightProfile?: {
    url: string;
    fileName: string;
    note?: string;
    addedAt?: string; // YYYY-MM-DD
  };

  homeBase: string;
  hourlyRate: number;
  cfiDiscountRate?: number; // $/Hobbs-hr for promo-plan flights
  // Fuel pass-through (gated). When enabled, each flight adds a fuel line =
  // Hobbs hours × fuelBurnGph × fuelPricePerGal, billed at cost (no markup).
  fuelBillingEnabled?: boolean;
  fuelBurnGph?: number; // gallons/Hobbs-hr (e.g. 8.5 for a 172C)
  fuelPricePerGal?: number; // $/gal (e.g. 7.75 at Signature)
  awayFromBaseHoursPerDay: number;
  annualDueDate: string;
  /**
   * A scheduled shop window (e.g. the annual): the airplane is physically at the
   * shop, so booking is blocked for EVERYONE — owner included — and the calendar
   * shows a red block. Start inclusive, end exclusive, YYYY-MM-DD. Clear the
   * fields when the airplane is back in service.
   */
  maintWindowStart?: string;
  maintWindowEnd?: string;
  maintWindowLabel?: string;
  currentHobbs: number;
  currentTach: number;
  // Hobbs/tach integrity audit trail. The admin is the SOLE editor of the
  // master reading (AdminAircraft) — every edit that moves currentHobbs/
  // currentTach appends an entry here (who/when/why), so a post-maintenance
  // gauge swap or correction is fully accountable. Newest-last (append order).
  readingAudit?: ReadingAuditEntry[];
  oilSpec?: string; // e.g. "Phillips 100AW · 8 qt · Tempest AA48108-2" — shown to lessees
  insuredValue?: number;
  openPilotWarranty?: OpenPilotWarranty;
  status: 'active' | 'maintenance' | 'retired';
  groundedReason?: string;
  // Direct owner ground/return-to-service (independent of squawks). When true the
  // aircraft is grounded — booking + flight logging are blocked for everyone until
  // the owner returns it to service. The aircraft is also grounded by any open
  // grounding squawk; this is the manual override the owner controls directly.
  groundedByOwner?: boolean;
}

// One audit entry on AircraftDoc.readingAudit[] — recorded whenever an admin
// changes the aircraft's master Hobbs/tach reading. `reason` is required by the
// UI (the "Why is the reading changing?" affordance), so a reading change is
// never silent.
export interface ReadingAuditEntry {
  at: string; // ISO timestamp (client clock — array entries can't use serverTimestamp)
  byUid: string;
  byName?: string;
  field: 'hobbs' | 'tach' | 'both';
  fromHobbs?: number;
  toHobbs?: number;
  fromTach?: number;
  toTach?: number;
  reason: string;
}

// readingDiscrepancies/{id} — the Hobbs/tach integrity record. Written
// SERVER-SIDE by onFlightLogged when a logged flight's observed start reading
// doesn't match the aircraft's expected current value (someone before this
// pilot forgot to log). The pilot is NOT billed for the gap — they're billed
// only from their own observed start — and the owner is notified to reconcile
// the master reading. Resolution fields are admin-written.
export type ReadingDiscrepancyField = 'hobbs' | 'tach';

export interface ReadingDiscrepancyDoc {
  discrepancyId: string;
  aircraftId: string;
  tailNumber?: string; // denormalized for the admin queue
  field: ReadingDiscrepancyField; // which gauge mismatched
  expected: number; // the system's current value (ac.currentHobbs/currentTach)
  observed: number; // what the pilot read off the gauge (the flight's start)
  delta: number; // observed − expected (signed)
  flightId?: string;
  byUid: string; // the pilot who logged the flight that surfaced this
  byName?: string;
  ts: Timestamp; // when the discrepancy was recorded
  resolved: boolean;
  // ── Resolution (admin-written) ──
  resolvedByUid?: string;
  resolvedByName?: string;
  resolvedAt?: Timestamp;
  resolutionNote?: string;
}

export type FuelLevel = '1/4' | '1/2' | '3/4' | 'Full';

// A shared, non-financial logbook entry written alongside each flight so EVERY
// pilot can see the aircraft's recent activity (who flew, when, route, readings).
// Deliberately excludes charges and notes — those stay on the private FlightDoc.
export interface FlightLogDoc {
  logId: string;
  flightId: string;
  aircraftId: string;
  pilotUid: string;
  pilotName: string;
  date: string;
  hobbsOut: number;
  hobbsIn: number;
  tachOut: number;
  tachIn: number;
  hoursFlown: number;
  departureAirport: string;
  destinations: string[];
  createdAt: Timestamp;
}

export interface FlightDoc {
  flightId: string;
  aircraftId: string;
  lesseeUid: string;
  date: string;
  hobbsOut: number;
  hobbsIn: number;
  tachOut: number;
  tachIn: number;
  hoursFlown: number;
  departureAirport: string;
  destinations: string[];
  passengerCount: number;
  fuelLevelOnReturn?: FuelLevel;
  notes?: string;
  baseCharge: number;
  awayFromBaseCharge: number;
  fuelCharge?: number; // pass-through fuel for this flight (hours × gph × $/gal)
  fuelGallonsLeft?: number; // gallons remaining in each tank after the flight
  fuelGallonsRight?: number;
  totalCharge: number;
  // How this flight is billed (server is authoritative; this records the intent).
  //   'standard' lessee overage · 'owner' never billed · 'promo' $75/hr · 'checkout' legacy (CFI free, retired 10/10/26)
  //   'comped' — complimentary guest flight, never billed
  billingMode?: 'standard' | 'owner' | 'promo' | 'checkout' | 'comped';
  /**
   * How the pilot elected to settle this flight (Casey, 2026-08-28 — Jacob asked).
   * 'card' (default) charges the card on file through the usual server flow.
   * 'cash' skips the Stripe charge and books the amount as an OPEN receivable the
   * owner marks paid when the money is physically handed over. It is never a
   * discount and never waives the debt.
   * Ignored for owner/comped/checkout flights, which are never billed either way.
   */
  paymentMethod?: 'card' | 'cash';
  // For a CFI ('checkout' billingMode): was this a pilot checkout flight (true) or
  // the CFI's own flight (false)? Both are free; this is for the record.
  isCheckoutFlight?: boolean;
  stripeChargeId?: string;
  createdAt: Timestamp;
  isCorrected?: boolean;
  correctionNotes?: string;
  // Set when the owner/admin logged this flight ON BEHALF of the lessee (they
  // forgot to log). Billing is identical — the lessee's card is charged — this
  // just records who actually entered it, for the audit trail.
  loggedByUid?: string;
  loggedByName?: string;
  // ADS-B flight track captured at log time (downsampled [lat,lon] path + max alt ft).
  // adsb.lol's free traces reset at UTC midnight, so we grab it when the flight is
  // logged (right after landing) and store it permanently on the flight record.
  track?: [number, number][];
  trackMaxAlt?: number;
}

export type BookingStatus =
  | 'auto_confirmed'
  | 'pending_approval'
  | 'approved'
  | 'denied'
  | 'cancelled'
  | 'completed';

/** Owner-set maintenance holds are ordinary bookings carrying this label — they
 *  render red on every calendar and block the slot exactly like a booking does. */
export interface BookingDoc {
  bookingId: string;
  aircraftId: string;
  lesseeUid: string;
  // Denormalized at booking time so other pilots can coordinate (call the
  // person ahead of them if running behind) without reading each other's user docs.
  lesseeName?: string;
  lesseePhone?: string;
  startDateTime: Timestamp;
  endDateTime: Timestamp;
  status: BookingStatus;
  maintenanceLabel?: string; // owner-only: red maintenance hold (e.g. "Annual Inspection")
  expectedDestinations?: string;
  notes?: string;
  createdAt: Timestamp;
  approvedAt?: Timestamp;
  approvedBy?: string;
  denialReason?: string;
  // Why a booking was cancelled. Lessee self-cancels = 'lessee_request'. When the
  // OWNER cancels, they must affirm it's at the lessee's request OR for asset
  // protection (maintenance/airworthiness/insurance) — never a discretionary
  // override of the reservation (operational-control guard for the dry lease).
  cancelReason?: 'lessee_request' | 'asset_protection';
  // When the cancellation was made. Without this, a booking dropped three days
  // out is indistinguishable from one dropped twenty minutes before the slot —
  // the difference between giving the time back and simply not showing up.
  // Absent on bookings cancelled before this field existed.
  cancelledAt?: Timestamp;
}

export type SquawkSeverity = 'informational' | 'should_fix_soon' | 'grounding';
export type SquawkStatus =
  | 'open'
  | 'acknowledged'
  | 'in_progress'
  | 'resolved'
  | 'cannot_reproduce'
  | 'not_an_issue';

export interface SquawkDoc {
  squawkId: string;
  aircraftId: string;
  reportedBy: string;
  reportedByName?: string; // denormalized so all pilots see who reported it
  severity: SquawkSeverity;
  description: string;
  photos: string[];
  dateDiscovered: string;
  status: SquawkStatus;
  adminNotes?: string;
  isGrounding: boolean;
  // For grounding billing: whose fault was the grounding? Default no_fault →
  // the grounded days pro-rate the lessees' monthly minimum (they get a credit).
  // Owner marks 'lessee_caused' (one-tap, editable even after resolve) → that
  // grounding bills full per lease §10.7. Only meaningful when isGrounding.
  groundingFault?: 'no_fault' | 'lessee_caused';
  createdAt: Timestamp;
  resolvedAt?: Timestamp;
}

export type DocumentType =
  | 'pilot_certificate'
  | 'pilot_certificate_back'
  | 'medical'
  | 'bfr_endorsement'
  | 'drivers_license'
  | 'renters_insurance'
  | 'lgb_badge'
  | 'lease'
  | 'cc_authorization'
  | 'checkout'
  | 'other';

export type VerificationStatus = 'pending' | 'verified' | 'rejected';

export interface DocumentDoc {
  documentId: string;
  ownerUid: string;
  type: DocumentType;
  label?: string;
  fileRef: string;
  fileName?: string;
  expirationDate?: string;
  verificationStatus: VerificationStatus;
  rejectionReason?: string;
  uploadedAt: Timestamp;
  verifiedAt?: Timestamp;
  verifiedBy?: string;
}

// A legally-binding electronic signature on the lease agreement.
// WRITE-ONCE: created at signing, never updated or deleted (enforced in rules).
// Stores the rendered agreement text VERBATIM (what the pilot actually saw,
// with their details merged in) — that, plus the immutable record, is what
// makes it defensible under the ESIGN Act / UETA.
export interface LeaseSignatureDoc {
  signatureId: string;
  signerUid: string;
  signerName: string; // printed/typed legal name
  signerEmail: string;
  aircraftId: string;
  agreementVersion: string;
  agreementText: string; // the exact rendered agreement the signer saw
  agreementHash: string; // SHA-256 of agreementText — integrity check
  signatureImageRef: string; // Storage URL of the drawn/typed signature image
  signatureMethod: 'drawn' | 'typed';
  consentAcknowledged: boolean; // explicit "I agree + consent to e-sign"
  ipAddress?: string;
  userAgent?: string;
  signedAt: Timestamp; // server time, never client clock
}

export type TransactionType =
  | 'flight_charge'
  | 'away_from_base'
  | 'fuel'
  | 'grounding_credit' // retired 8/23/2026 with the monthly minimum; 2 historical docs remain
  | 'custom'
  | 'refund';
export type TransactionStatus = 'succeeded' | 'failed' | 'refunded' | 'disputed' | 'cash_due';

export interface TransactionDoc {
  transactionId: string;
  lesseeUid: string;
  aircraftId: string;
  flightId?: string;
  cycleId?: string;
  type: TransactionType;
  amount: number;
  description?: string;
  stripeChargeId?: string;
  status: TransactionStatus;
  failureReason?: string;
  reason?: string;
  // Dunning state (server-managed): retry attempt count, when the next retry is
  // due, and where to write stripeChargeId back on eventual success.
  attempt?: number;
  nextRetryAt?: Timestamp;
  flightDocPath?: string;
  pastDue?: boolean;
  // Deterministic idempotency key for a charge that isn't tied to a flight. Doubles
  // as the dunning retry-key base so a retry can't collide across users. Absent on
  // flight charges.
  dedupeKey?: string;
  /**
   * Cash settlement (Casey, 2026-09-09 — Jacob asked how a cash payment shows up).
   * A flight the pilot elected to pay in cash is booked as `status 'cash_due'`; when
   * the owner taps "Mark paid" it flips to 'succeeded' and `cashReceivedAt` is
   * stamped. The server then emails the pilot a cash receipt (once, guarded by
   * `receiptEmailSentAt`). A doc id starting with `cash_` is always a cash ledger
   * entry; `paymentMethod` is set on the same write for the UI.
   */
  paymentMethod?: 'card' | 'cash';
  cashReceivedAt?: Timestamp;
  receiptEmailSentAt?: Timestamp;
  /** Sequential receipt number (AJM-1001, AJM-1002, …), server-assigned on success. */
  receiptNo?: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

// A tracked maintenance / airworthiness item for the aircraft. Driven by a
// calendar due date, a Hobbs-hour threshold, or both. Overdue airworthiness
// items ground the plane. `lastAlertLevel` is server-managed (the scheduled
// alert function bumps it as the item escalates, so the owner isn't spammed).
export type MaintenanceTrack = 'date' | 'hours' | 'both';

export interface MaintenanceItemDoc {
  itemId: string;
  aircraftId: string;
  label: string;
  track: MaintenanceTrack;
  dueDate?: string; // YYYY-MM-DD (track date/both)
  dueHobbs?: number; // Hobbs reading at which it's due (track hours/both)
  intervalDays?: number; // used to recompute the next due when marked complete
  intervalHours?: number;
  lastDoneDate?: string;
  lastDoneHobbs?: number;
  isAirworthiness: boolean; // overdue → grounds the aircraft
  deferredUntil?: string; // YYYY-MM-DD — knowingly postponed (e.g. oil folded into the annual).
  // Hides the item from pilot-facing "next due" chips and owner alert emails until this
  // date passes, then it releases itself automatically. NEVER suppresses grounding:
  // airworthiness items can't be deferred (enforced in the admin UI).
  deferReason?: string;
  notes?: string;
  docUrl?: string; // optional scanned work order / logbook page (Firebase Storage)
  lastAlertLevel?: number; // 0 ok · 1 due-soon · 2 due-now · 3 overdue (server-managed)
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

/** Which book a maintenance logbook entry was written in. */
export type LogbookBook = 'airframe' | 'engine' | 'propeller' | 'avionics' | 'other';

export interface LogbookPage {
  /** Storage path (maintenance/{aircraftId}/logbook/...), or a blob: URL in the demo. */
  path: string;
  name: string;
  contentType?: string;
  size?: number;
}

/**
 * A photographed maintenance logbook entry — aircraftLogbook/{entryId}.
 * NOT the pilot logbook (logbookEntries). The paper books stay the legal
 * record; this is the copy findable from a phone. Pages are Storage paths,
 * resolved at render time (storageUrl.ts), never download URLs.
 */
export interface AircraftLogbookEntryDoc {
  entryId: string;
  aircraftId: string;
  book: LogbookBook;
  /** Date written in the book (YYYY-MM-DD) — not the date it was photographed. */
  date: string;
  title: string;
  tach?: number;
  hobbs?: number;
  notes?: string;
  pages: LogbookPage[];
  /** Filled in by "Read it for me" (Claude) and reviewed by the owner before saving. */
  readByAi?: boolean;
  createdAt: Timestamp;
  createdByUid?: string;
  updatedAt?: Timestamp;
}

export interface BillingCycleDoc {
  cycleId: string;
  lesseeUid: string;
  startDate: string;
  endDate: string;
  hoursFlown: number;
  totalCharges: number;
  status: 'active' | 'closed';
  closedAt?: Timestamp;
}


export interface AuditLogDoc {
  logId: string;
  userId: string;
  action: string;
  entityType: string;
  entityId: string;
  oldValue?: unknown;
  newValue?: unknown;
  timestamp: Timestamp;
  ip?: string;
  userAgent?: string;
}

export interface ConfigDoc {
  registrationPin: string;
  businessName: string;
  businessAddress: string;
  businessContactEmail: string;
  businessContactPhone: string;
  updatedAt: Timestamp;
  updatedBy?: string;
}

// ───────────────────────── CFI checkout sign-off ─────────────────────────
// A carrier-named CFI's record that they checked a renter out in N8366X. Two acts,
// two timestamps, deliberately separate: the CFI certifies competency (signedAt);
// the owner, as Named Insured, then approves the pilot under the Policy's open-pilot
// provisions (approvedAt). Only active users with billingProfile 'cfi' may create
// these (enforced in firestore.rules) — that role today is exactly the instructors
// named on the policy's Approved Flight Instructor Amendment.
export interface CheckoutDoc {
  checkoutId: string;
  aircraftId: string;
  pilotUid: string;
  pilotName: string;
  cfiUid: string;
  cfiName: string;
  cfiCertNumber: string;
  /**
   * Who actually signed. The CFI checkout is AJM's own rule (lease §4.3(d)) — it is
   * NOT an Old Republic requirement, since PB214's required-training box is blank —
   * so the owner may sign one himself. When he does, the record has to say so
   * rather than sit in the cfi* fields implying a carrier-named instructor did it.
   */
  signedByRole?: 'cfi' | 'owner';
  date: string; // YYYY-MM-DD of the (final) checkout flight
  hobbs?: number;
  dualHoursGiven: number;
  // What the pilot holds — recorded so the sign-off shows the standard applied
  // ("maneuvers demonstrated to the standard of the certificate held").
  pilotCertificate: string;
  groundItems: string[]; // keys from CHECKOUT_GROUND_ITEMS, all required
  flightItems: string[]; // keys from CHECKOUT_FLIGHT_ITEMS, all required
  /**
   * The scope this checkout grants — the club-standard model: one universal core
   * checkout for every pilot, with privilege add-ons signed off only when their
   * section was actually covered. 'vfr_day' is always present; 'night' and 'ifr'
   * require their item lists complete.
   */
  privileges: Array<'vfr_day' | 'night' | 'ifr'>;
  nightItems?: string[]; // keys from CHECKOUT_NIGHT_ITEMS — required iff 'night'
  ifrItems?: string[]; // keys from CHECKOUT_IFR_ITEMS — required iff 'ifr'
  notes?: string;
  signatureName: string; // typed by the CFI; their attestation
  signedAt: Timestamp; // server time
  status: 'submitted' | 'approved';
  approvedAt?: Timestamp;
  approvedBy?: string;
}

// The core applies to EVERY pilot regardless of certificate — a commercial pilot
// demonstrates the same maneuvers, to the standard of the certificate they hold.
/**
 * The checkout is about THIS airplane, not about being a pilot.
 *
 * Rewritten 2026-09-03 (Casey): the earlier list was private-pilot training —
 * slow flight, stalls, steep turns, short/soft field. Every renter here already
 * holds the certificate. What most of them have NOT done is fly glass: dual G5s
 * with no vacuum system, a GFC 500 autopilot, and an EDM 900 as the primary
 * engine instrument. The single most important item on this list is knowing every
 * way to get the autopilot to let go of the airplane.
 */
export const CHECKOUT_GROUND_ITEMS: Record<string, string> = {
  autopilot:
    'GFC 500 autopilot — every way to disconnect (yoke A/P DISC, the AP button, the breaker), the modes and what the flight director is showing, recognising a runaway or a mode you did not ask for, and LVL as the recovery.',
  glass:
    'Dual G5s and the all-electric panel — no vacuum system, the G5 backup batteries, and what you lose and keep on an alternator or battery failure.',
  navigators:
    'GNX 375 and GNC 255A — which box is the IFR GPS and ADS-B, which flies VOR/LOC/ILS, and switching the CDI source so the autopilot follows what you intend.',
  edm900:
    'JPI EDM 900 as the primary engine instrument — leaning, CHT and EGT limits, and why the totalizer only knows what you told it. Cross-check the tanks.',
  o300_fuel:
    'Continental O-300 and fuel — carb heat and carb ice, cold and hot starts, 100LL, 36 gal usable of 39, weight & balance (1,436 lb empty, 814 useful), and V-speeds: Va 100 · Vno 122 · Vne 139 · Vfe 87 kt.',
  ajm_klgb:
    'AJM and KLGB — tie-down at Million Air North, both tanks topped, logging every flight, squawks and grounding authority.',
};

export const CHECKOUT_FLIGHT_ITEMS: Record<string, string> = {
  ap_inflight:
    'Autopilot in flight — engage each mode, disconnect by every means including a simulated runaway, and a go-around with it still engaged. Hand-fly straight after without chasing it.',
  partial_panel: 'Simulated G5 / display failure — partial panel on what remains.',
  landings: 'Normal and crosswind takeoffs and landings at KLGB.',
  emergencies: 'Simulated engine failure and off-field approach, plus an electrical failure.',
};

/**
 * Retired item keys, kept so sign-offs recorded before a list change still render
 * the wording the CFI actually signed instead of a bare key.
 */
export const CHECKOUT_LEGACY_ITEMS: Record<string, string> = {
  // Original list (private-pilot maneuvers), retired 2026-09-03.
  systems: 'Aircraft systems and the Continental O-300 — power settings, leaning, cold/hot starts',
  taa: 'Glass panel and TAA equipment — Garmin displays, GNX 375 GPS basics, autopilot modes and disconnects',
  fuel_wb: 'Fuel system and capacity, weight & balance, and performance planning',
  speeds: 'V-speeds, limitations, and normal and emergency procedures per the POH/AFM',
  local: 'KLGB procedures, tie-down at Million Air North, and securing the aircraft',
  portal: 'AJM procedures: logging every flight, squawks, grounding authority, and fuel top-off at KLGB',
  takeoffs_landings: 'Normal and crosswind takeoffs and landings',
  short_soft: 'Short- and soft-field takeoffs and landings',
  slow_stalls: 'Slow flight and power-on / power-off stalls',
  steep_turns: 'Steep turns',
  go_around: 'Go-arounds and rejected landings',
  // Intermediate list, same day — split too finely to fill in on an iPad.
  ap_disconnect: 'GFC 500 — every way to disconnect, runaway trim, and LVL as the recovery tool',
  ap_modes: 'GFC 500 modes — HDG/TRK, NAV, APR, IAS, VS, ALT and ALT SEL',
  g5_glass: 'Dual Garmin G5 — PFD and HSI, no vacuum system, backup batteries',
  electrical: 'All-electric panel: alternator or battery failure, avionics master, load shedding',
  o300: 'Continental O-300 — carb heat and carb ice, cold and hot starts, power settings, fuel grades',
  ap_goaround: 'Go-around with the autopilot engaged',
  g5_failure: 'Simulated G5 / display failure — partial panel and reversion',
  gps_nav: 'GNX 375 in flight — direct-to, flight plan, CDI source switching',
  leaning: 'Leaning in cruise on the EDM 900',
};

// Signed off only when the checkout grants NIGHT privileges.
export const CHECKOUT_NIGHT_ITEMS: Record<string, string> = {
  night_systems: 'Aircraft and airport lighting, electrical system and failures at night',
  night_landings: 'Night takeoffs and full-stop landings',
  night_local: 'Local terrain, obstructions, and lighting around the LA basin at night',
};

// Signed off only when the checkout grants IFR privileges — the club-standard
// additional dual segment for instrument-rated pilots intending to file.
export const CHECKOUT_IFR_ITEMS: Record<string, string> = {
  ifr_gps: 'GNX 375 IFR operations — flight plan, procedure loading, LPV/LNAV approach setup and flying',
  ifr_coupled: 'Autopilot-coupled approach, and hand-flown approach to minimums',
  ifr_missed_hold: 'Missed approach and holding, including GPS sequencing',
  ifr_partial: 'Partial-panel / equipment-failure procedures in this panel',
  ifr_legal: 'IFR fuel and alternate planning, and the VOR check — logged on the sheet in the aircraft binder',
};

export const DOCUMENT_TYPE_LABEL: Record<DocumentType, string> = {
  pilot_certificate: 'Pilot Certificate (front)',
  pilot_certificate_back: 'Pilot Certificate (back — ratings)',
  medical: 'Medical Certificate',
  bfr_endorsement: 'Flight Review Endorsement',
  drivers_license: "Driver's License",
  renters_insurance: 'Renter’s Insurance',
  lgb_badge: 'LGB Airport Badge',
  lease: 'Signed Lease',
  cc_authorization: 'Credit Card Authorization',
  checkout: 'CFI Checkout Sign-off',
  other: 'Other',
};

// These three gate ONBOARDING. Renter's insurance is NOT on this list — but it is
// required by lease §11.2 before solo PIC, and in any event within 14 days of the
// Effective Date. It sits outside onboarding only so a pilot can finish setting up
// and fly dual with a named CFI while his policy is being written.
//
// The earlier note here said insurance was optional because "the commercial policy
// covers renters". That is wrong and it is the misconception §11.6 exists to correct:
// AJM's hull coverage insures AJM, and Old Republic may subrogate against the pilot
// for damage he causes. See src/lib/soloGate.ts for the notice that enforces §11.2.
//
// The LGB badge is not a blocker at onboarding, because §9.3 allows a pilot without one
// to be escorted onto the ramp by someone who holds a current badge. It is NOT optional in
// any larger sense: §9.3 makes obtaining and paying for a badge the Lessee's obligation,
// and without one they need an escort every single time they go to the airplane.
export const REQUIRED_DOCUMENT_TYPES: DocumentType[] = [
  'pilot_certificate',
  // The back is where AIRPLANE SINGLE ENGINE LAND and any instrument rating are
  // printed. PB214 conditions coverage on the pilot holding "the appropriate
  // ratings", and lease §4.3(a) requires ASEL by name — neither is verifiable
  // from the front of the card.
  'pilot_certificate_back',
  'medical',
  'drivers_license',
];

// ───────────────────────── Pilot logbook ─────────────────────────
// One flight row in a pilot's digital logbook. Mirrors a standard Jeppesen/FAA
// logbook spread (left+right pages = one row). All time fields are decimal hours.
// Editable at any time. `source` = how it got here; `needsReview` flags validation
// issues (bad OCR / impossible math) for the user to fix.
export interface LogbookEntryDoc {
  entryId: string;
  ownerUid: string;
  date: string; // YYYY-MM-DD
  aircraftType?: string; // make & model, e.g. "C-172"
  ident?: string; // tail number
  from?: string;
  to?: string;
  route?: string; // optional full multi-leg route
  // counts
  dayLandings?: number;
  nightLandings?: number;
  takeoffs?: number;
  approaches?: number; // instrument approaches
  // category & class (hours)
  asel?: number; // single-engine land
  amel?: number; // multi-engine land
  ases?: number; // single-engine sea
  ames?: number; // multi-engine sea
  // conditions of flight (hours)
  night?: number;
  actualInstrument?: number;
  simInstrument?: number; // simulated / hood
  simulator?: number; // FFS / FTD
  // type of piloting time (hours)
  crossCountry?: number;
  solo?: number;
  dualReceived?: number;
  pic?: number;
  sic?: number;
  instructorGiven?: number; // CFI given
  total?: number; // total duration of flight
  remarks?: string;
  endorsements?: string;
  photos?: string[]; // attachment urls (trip pics, fuel receipts)
  mapUrl?: string; // ADS-B flight-track screenshot
  hobbsOut?: number;
  hobbsIn?: number;
  source?: 'manual' | 'flight' | 'photo';
  flightId?: string; // when derived from a logged flight
  needsReview?: boolean;
  reviewNote?: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

/**
 * A named CFI's per-flight approval of a STUDENT pilot's booking — Old Republic's
 * Student Pilot Restriction: "All Student Pilots must operate the aircraft under the
 * supervision of a Flight Instructor who is properly certificated by the FAA and who
 * shall have specifically approved each flight by the Student prior to take-off."
 * Wings confirmed in writing 10/15/2026 (CFI must be on PB222; no extra premium).
 *
 * Doc id = bookingId. approvedStart/End copy the booking's times at approval, so a
 * booking moved afterwards no longer matches and reads as "needs re-approval".
 * Only an active carrier-named CFI may create one (firestore.rules) — never the
 * owner, who isn't a CFI.
 */
export interface FlightApprovalDoc {
  bookingId: string;
  aircraftId: string;
  pilotUid: string;
  pilotName: string;
  cfiUid: string;
  cfiName: string;
  cfiCertNumber: string;
  kind: 'dual' | 'solo';
  approvedStart: Timestamp;
  approvedEnd: Timestamp;
  /** Solo only: CFI confirmed the student's §61.87 endorsements and medical are current. */
  soloEndorsementsConfirmed?: boolean;
  notes?: string;
  signatureName: string;
  approvedAt: Timestamp;
}
